import { computed, ref, watch, toValue } from "vue";
import type { MaybeRefOrGetter } from "vue";
import { useQuery } from "@urql/vue";
import type {
  ResultOf,
  TypedDocumentNode,
} from "@graphql-typed-document-node/core";
import { graphQlClient } from "@/utils/urql";

export function useInfiniteQuery<Q extends TypedDocumentNode<any, any>, T>({
  query,
  variables,
  pause,
  onData,
  extractData,
  dedupeKey,
  limit = 10,
}: {
  query: Q;
  variables: MaybeRefOrGetter<ResultOf<Q>["variables"]>;
  pause?: MaybeRefOrGetter<boolean>;
  onData?: (data: T[]) => void;
  extractData?: (result: ResultOf<Q>) => T[];
  /**
   * Optional stable identity for an item. When provided, the accumulated list
   * is de-duplicated by this key on every merge — later occurrences are
   * dropped. Use for paginated lists whose pages can briefly overlap across
   * separate refetch requests (e.g. an item shifting across a page boundary
   * when the underlying list changes between two `refetchAllPages` fetches),
   * so a keyed virtual list never receives two rows with the same id.
   */
  dedupeKey?: (item: T) => string;
  limit: number;
}) {
  const page = ref(0);
  const allItems = ref<T[]>([]);
  const hasInitialLoad = ref(false);
  const hasMore = ref(true);
  // Synchronous in-flight guard. `loading` (urql `fetching`) only flips to true
  // after the reactive query re-evaluates on the next tick, so a rapid-fire
  // caller (e.g. the 300ms infinite-scroll emitter on a short/empty rail) can
  // increment `page` several times before `loading` ever reads true. This flag
  // is set the instant a page is requested and cleared once that page settles,
  // so re-entrant `loadMore()` calls within the same tick are dropped.
  const pageInFlight = ref(false);

  function dedupe(items: T[]): T[] {
    if (!dedupeKey) return items;
    const seen = new Set<string>();
    const out: T[] = [];
    for (const item of items) {
      const key = dedupeKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  }

  const {
    data: queryResult,
    fetching: loading,
    executeQuery: refetch,
  } = useQuery({
    query: query,
    variables: computed(() => ({
      ...toValue(variables),
      page: page.value,
      limit: limit,
    })),
    pause,
    requestPolicy: "network-only",
  });

  // Reset when non-pagination variables change
  watch(
    () => toValue(variables),
    () => {
      reset();
      refetch();
    },
    { deep: true },
  );

  watch(queryResult, (newResult) => {
    // The requested page has settled (success or empty): release the in-flight
    // guard so the next genuine `loadMore()` can proceed.
    pageInFlight.value = false;

    if (!newResult) {
      hasMore.value = false;
      return;
    }

    const newItems = extractData ? extractData(newResult) : (newResult as T[]);
    if (page.value === 0) {
      allItems.value = dedupe(newItems);
    } else {
      allItems.value = dedupe([...allItems.value, ...newItems] as T[]);
    }

    // A page is the last page once the server returns fewer items than the
    // requested page size (a full page of exactly `limit` items means there may
    // still be more). This makes an empty first page (length 0 < limit) settle
    // `hasMore=false` immediately, so no further pages are ever requested. Once
    // false it stays false until `reset()` (variables change / refetch).
    hasMore.value = newItems.length >= limit;

    if (onData) {
      onData(allItems.value as T[]);
    }
  });

  async function refetchAllPages() {
    const fresh: T[] = [];
    let freshHasMore = true;

    for (let i = 0; i <= page.value; i++) {
      const { data: _data, error } = await graphQlClient.value.query(
        query,
        { ...toValue(variables), page: i, limit: limit },
        { requestPolicy: "network-only" },
      );
      const data = toValue(_data);

      if (!data) {
        console.error("Failed to fetch data for page", i, error);
        return;
      }

      const items = extractData ? extractData(data) : (data as T[]);
      fresh.push(...items);

      // Same rule as the reactive `loadMore` path above: a page shorter than
      // `limit` (including empty) is the last page.
      freshHasMore = items.length >= limit;
      if (!freshHasMore) {
        break;
      }
    }

    allItems.value = dedupe(fresh);
    hasMore.value = freshHasMore;
  }

  function loadMore() {
    // `pageInFlight` is the synchronous re-entrancy guard; `loading` covers the
    // post-tick async fetch window. Both must be clear, and there must be more
    // to load. Set `pageInFlight` BEFORE the await-driven `loading` flips so a
    // burst of same-tick calls only advances the page once.
    if (pageInFlight.value || loading.value || !hasMore.value) {
      return;
    }
    pageInFlight.value = true;
    page.value++;
    hasInitialLoad.value = true;
  }

  function reset() {
    page.value = 0;
    allItems.value = [];
    hasInitialLoad.value = false;
    hasMore.value = true;
    pageInFlight.value = false;
  }

  return {
    items: computed(() => allItems.value),
    loading,
    loadMore,
    reset,
    hasInitialLoad,
    refetch: refetchAllPages,
    hasMore,
  };
}
